import * as t from 'io-ts';

import {RCellsConversationsRange, RCellsMessagesRange} from '../../../helpers/cellsHelpers';
import {RConversationIdRef, RMessageUidRef} from '../../../models/api/apiTypes';
import {RConversationHighlight} from '../../../models/api/conversationHighlightModel';
import {RConversationOverlay} from '../../../models/api/conversationOverlayModel';
import {
  ConversationSearchResultModel,
  RConversationSearchResult,
} from '../../../models/api/conversationSearchResultModel';
import {makeResourceMap, RAsyncMoreStatus, RAsyncStatus, ResourceMap} from '../../appTypes';
import {
  hasSearchInInboxesParameters,
  InboxesConversationsParametersWithSearch,
  RInboxesConversationsParameters,
  RInboxesTeammateMessagesParameters,
} from '../inboxes/parameters/inboxesParameters';
import {RPanelCancelTokenId} from '../panelCancelTokenId';
import {PanelTypesEnum, RCellsPanelId, RPanelStateBase} from '../panelStateBase';
import {RCellsContext} from './context/cellsContext';
import {RCellsPanelSearchState} from './search/cellsPanelSearchState';

/*
 * Runtime types.
 */

export enum CellsPanelPageTypesEnum {
  CONVERSATIONS = 'CONVERSATIONS',
  MESSAGES = 'MESSAGES',
}

const RCellsPanelPageStateBase = t.type({
  status: RAsyncStatus,
  isLoadedFromCache: t.boolean,
  isRefetching: t.boolean,
  moreStatusTop: RAsyncMoreStatus,
  moreStatusBottom: RAsyncMoreStatus,
  cellsContext: RCellsContext,
});

const RCellsPanelConversationsPageState = t.intersection([
  RCellsPanelPageStateBase,
  RCellsConversationsRange,
  t.type({
    uid: t.string,
    type: t.literal(CellsPanelPageTypesEnum.CONVERSATIONS),
    parameters: RInboxesConversationsParameters,
    // This is not guaranteed to be sorted, and should be treated as an unsorted set of conversation IDs!
    conversationIds: t.readonlyArray(RConversationIdRef),
    conversationHighlights: makeResourceMap(t.Integer, RConversationHighlight),
    conversationMatchedQueries: makeResourceMap(t.Integer, t.readonlyArray(t.string)),
    /**
     * When conversationSearchResults is defined and hasSearchInInboxesParameters is true,
     * we'll use the former to display the list of conversations.
     * {@see stateIsCellsPanelPageConversationStateWithSearchResults} below.
     */
    conversationSearchResults: t.union([t.undefined, makeResourceMap(t.Integer, RConversationSearchResult)]),
    removedConversationIds: t.readonlyArray(t.Integer),
    hiddenConversationId: t.union([t.Integer, t.null]),
    conversationOverlays: makeResourceMap(t.Integer, RConversationOverlay),
  }),
]);

const RCellsPanelMessagesPageState = t.intersection([
  RCellsPanelPageStateBase,
  RCellsMessagesRange,
  t.type({
    uid: t.string,
    type: t.literal(CellsPanelPageTypesEnum.MESSAGES),
    parameters: RInboxesTeammateMessagesParameters,
    messageUids: t.readonlyArray(RMessageUidRef),
  }),
]);

const RCellsPanelPageState = t.taggedUnion('type', [
  RCellsPanelConversationsPageState,
  RCellsPanelMessagesPageState,
]);

const RCellsPanelPagesState = t.type({
  current: RCellsPanelPageState,
  next: t.union([RCellsPanelPageState, t.undefined]),
});

export const RCellsPanelState = t.intersection([
  RPanelStateBase,
  t.type({
    id: RCellsPanelId,
    type: t.literal(PanelTypesEnum.CELLS),
    pages: RCellsPanelPagesState,
    search: RCellsPanelSearchState,
    cancelTokenId: RPanelCancelTokenId,
  }),
]);

/*
 * Types.
 */

export interface CellsPanelConversationsPageState
  extends Readonly<t.TypeOf<typeof RCellsPanelConversationsPageState>> {}
export interface CellsPanelMessagesPageState
  extends Readonly<t.TypeOf<typeof RCellsPanelMessagesPageState>> {}
export type CellsPanelPageState = CellsPanelConversationsPageState | CellsPanelMessagesPageState;
export interface CellsPanelPagesState extends Readonly<t.TypeOf<typeof RCellsPanelPagesState>> {}

export interface CellsPanelState extends Readonly<t.TypeOf<typeof RCellsPanelState>> {}

export type CellsPanelConversationsPageStateWithSearchResults = CellsPanelConversationsPageState & {
  readonly parameters: InboxesConversationsParametersWithSearch;
  readonly conversationSearchResults: ResourceMap<ConversationSearchResultModel>;
};

/*
 * Type guards.
 */

export function stateIsCellsPanelPageConversationState(
  state: CellsPanelPageState,
): state is CellsPanelConversationsPageState {
  return state.type === CellsPanelPageTypesEnum.CONVERSATIONS;
}

export function stateIsCellsPanelPageConversationStateWithSearchResults(
  state: CellsPanelPageState,
): state is CellsPanelConversationsPageStateWithSearchResults {
  return (
    stateIsCellsPanelPageConversationState(state) &&
    Boolean(hasSearchInInboxesParameters(state.parameters) && state.conversationSearchResults)
  );
}
